// Tracks the QuickBooks module selector (collections | weighbridge | skip)
// shared between the Settings and Sync pages. Persisted in sessionStorage so
// the choice survives navigation between the two pages within a tab, but
// resets between browser sessions. Default: collections.

import { useCallback, useState } from 'react';
import type { QbModule } from '../../../../../types/api';

export const QB_ACTIVE_MODULE_KEY = 'qb_active_module';

const MODULES: readonly QbModule[] = ['collections', 'weighbridge', 'skip'];

function readStored(): QbModule {
  try {
    const raw = sessionStorage.getItem(QB_ACTIVE_MODULE_KEY);
    if (raw && (MODULES as readonly string[]).includes(raw)) {
      return raw as QbModule;
    }
  } catch {
    // sessionStorage unavailable (SSR / privacy mode) — fall through.
  }
  return 'collections';
}

export function useQbActiveModule(): [QbModule, (module: QbModule) => void] {
  const [module, setModuleState] = useState<QbModule>(() => readStored());

  const setModule = useCallback((next: QbModule) => {
    setModuleState(next);
    try {
      sessionStorage.setItem(QB_ACTIVE_MODULE_KEY, next);
    } catch {
      // Ignore persistence failures — in-memory state still updates.
    }
  }, []);

  return [module, setModule];
}
